/* eslint-disable multiline-ternary */
import { yupResolver } from '@hookform/resolvers/yup'
import { Fragment, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import InputMask from 'react-input-mask'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap'
import { InputEmail, InputText } from '../../../../components/inputs'
import { SchemaValidations } from '../validations'
import { Edit, Eye } from 'react-feather'
import { editAction } from '../../store'
import { contributorTypedb } from '../../../customer-master/db/contributorType'
import { getCities, getDistricts } from '../../../invoice-issuance/thunks'
import { districtsOptions } from '../../../invoice-issuance/db/districts'
import Cleave from 'cleave.js/react'
import classnames from 'classnames'
import InputInternationalPhone from '../../../../components/inputs/internationalPhone'

const initialFilterProps = {
  clientCode: '',
  codeType: '',
  dteType: '',
  company: '',
  receiverEmail: '',
  receiverPhoneNumber: '',
  receiverName: '',
  // startDate: moment().subtract(1, 'day'),
  // endDate: null
}

const BusinessForm = ({ addLogo }) => {
  const [filterProps, setFilterProps] = useState(initialFilterProps)
  const [areaFilterValue, setAreaFilterValue] = useState(null)
  const [isEdited, setisEdited] = useState(false)
  const dispatch = useDispatch()
  const { data } = useSelector((state) => state.businessManager)
  const {
    departments,
    cities,
    districts,
    economicActivities,
    documentTypes,
    offerTypes,
    taxpayerTypes,
  } = useSelector((state) => state.invoiceIssuance)

  // CAT-091 desde el backend: sus id cambian entre ambientes. La lista
  // estática solo sirve de respaldo para no dejar el select vacío
  const hayTaxpayerTypes = Array.isArray(taxpayerTypes) && taxpayerTypes.length > 0
  const contributorOptions = hayTaxpayerTypes ? taxpayerTypes : contributorTypedb

  const [modalOpen, setModalOpen] = useState(false)

  // Función para abrir el modal
  const toggleModal = () => setModalOpen(!modalOpen)

  // useForm
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {},
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(SchemaValidations),
  })

  // console.log('FOR ERRORS: ', errors)

  const department = watch('department')
  const municipality = watch('municipality')

  useEffect(() => {
    if (department?.value) {
      dispatch(getCities(department?.value))
    }
  }, [dispatch, department])

  useEffect(() => {
    if (department?.value && municipality?.value) {
      dispatch(
        getDistricts({
          departmentCode: department?.value,
          municipalityCode: municipality?.value,
        })
      )
    }
  }, [dispatch, department, municipality])

  const districtOptions = Array.isArray(districts) && districts.length
    ? districts
    : districtsOptions.filter(district =>
        district.department === department?.value &&
        district.municipality === municipality?.value
      )

  const onSubmit = (formData) => {
    dispatch(editAction({ ...data, ...formData }))
    setisEdited(false)
  }

  const handleNewCustomer = () => {
    dispatch(setOpenModal({ open: true, type: 'create' }))
  }

  const handleEdit = (e) => {
    e.preventDefault()
    setisEdited(!isEdited)
  }

  const economicActivityWithCombinedLabel = (economicActivities || {}).map
    ? economicActivities.map((option) => ({
        ...option,
        label: `${option.value} - ${option.label}`,
      }))
    : []

  useEffect(() => {
    if (data) {
      reset(data) // Se pasa todo el objeto 'data' al reset
    }
  }, [data, reset])

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="flex-md-row flex-column align-md-items-center align-items-start border-bottom">
            <CardTitle tag="h4">
              Información principal de la empresa
            </CardTitle>
          </CardHeader>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <InputText
                label={'Nombre del cliente'}
                errors={errors}
                options={{
                  control,
                  id: 'clientName',
                  name: 'clientName',
                  disabled: !isEdited,
                }}
              />
            </Col>

            <Col
              md="6"
              sm="12"
            >
              <InputText
                label={'Sociedad'}
                errors={errors}
                options={{
                  control,
                  id: 'company',
                  name: 'company',
                  startWithCero: true,
                  disabled: !isEdited,
                }}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">NIT*</Label>
              <Controller
                id="nit"
                name="nit"
                control={control}
                render={({ field }) => {
                  return (
                    <InputMask
                      mask={'9999-999999-999-9'}
                      disabled={!isEdited}
                      placeholder="Número NIT"
                      className={classnames('form-control', {
                        'is-invalid':
                          data !== null &&
                          (data.documentNumber === null ||
                            !data.documentNumber?.length ||
                            errors.documentNumber),
                      })}
                      {...field} // Field props to manage the value and onChange
                    >
                      {() => (
                        <Input
                          {...field}
                          type="text"
                          className="form-control"
                          errors={errors}
                          control={control}
                          disabled={!isEdited}
                        />
                      )}
                    </InputMask>
                  )
                }}
              />
              {errors.nit && (
                <FormFeedback className="d-block">
                  {errors.nit.message}
                </FormFeedback>
              )}
            </Col>
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">NRC*</Label>
              <Controller
                id="nrc"
                name="nrc"
                control={control}
                rules={{
                  required: 'El NRC es requerido',
                  pattern: {
                    value: /^[0-9]{1,8}$/,
                    message:
                      'El NRC debe tener entre 1 y 8 dígitos numéricos',
                  },
                }}
                render={({ field }) => {
                  return (
                    <Cleave
                      options={{
                        numericOnly: true, // Solo permite números
                        delimiter: '-',
                        blocks: [8],
                      }}
                      className={classnames('form-control')}
                      disabled={!isEdited}
                      {...field}
                    />
                  )
                }}
              />
              <FormFeedback className="d-block">
                {errors?.nrc?.message}
              </FormFeedback>
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col>
              <Label className="me-1">Tipo de contribuyente</Label>
              <Controller
                name="largeConsumer"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={contributorOptions}
                      isDisabled={!isEdited}
                    />
                    {errors.largeConsumer && (
                      <FormFeedback className="d-block">
                        {errors.largeConsumer.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
            <Col>
              <Label className="me-1">Actividad económica</Label>
              <Controller
                name="economicActivity"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={economicActivityWithCombinedLabel}
                      isDisabled={!isEdited}
                      //value={filterProps.codeType ? { value: filterProps.codeType, label: filterProps.codeType === 'dui' ? 'DUI' : 'NIT' } : null}
                      /*onChange={(e) => {
                            field.onChange(e.value)
                        }}*/
                    />
                    {errors.economicActivity && (
                      <FormFeedback className="d-block">
                        {errors.economicActivity.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col>
              <Label className="me-1">Tipo de establecimiento</Label>
              <Controller
                name="typeEstablishment"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={[
                        { value: 'FE', label: 'FE' },
                        { value: 'CCFE', label: 'CCFE' },
                        { value: 'NRE', label: 'NRE' },
                        { value: 'NCE', label: 'NCE' },
                        { value: 'NDE', label: 'NDE' },
                        { value: 'CLE', label: 'CLE' },
                        { value: 'DCLE', label: 'DCLE' },
                        { value: 'FEXE', label: 'FEXE' },
                        {
                          value: 'electronicInvalidEvent',
                          label: 'Evento de invalidación electrónico',
                        },
                        {
                          value: 'electronicEvent',
                          label: 'Evento de contingencia electrónico',
                        },
                      ]}
                      isDisabled={!isEdited}
                    />
                    {errors.typeEstablishment && (
                      <FormFeedback className="d-block">
                        {errors.typeEstablishment.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">Tipo de producto</Label>
              <Controller
                name="typeOffer"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={offerTypes}
                      isDisabled={!isEdited}
                    />
                    {errors.typeOffer && (
                      <FormFeedback className="d-block">
                        {errors.typeOffer.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <InputEmail
                label="correo de recepción"
                errors={errors}
                control={control}
                options={{
                  id: 'incomingEmail',
                  name: 'incomingEmail',
                  disabled: !isEdited,
                }}
              />
            </Col>
            <Col
              md="6"
              sm="12"
            >
              <InputEmail
                label="correo de emisión"
                errors={errors}
                control={control}
                options={{
                  id: 'email',
                  name: 'email',
                  disabled: !isEdited,
                }}
              />
            </Col>
          </Row>

          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">Departamento</Label>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={departments}
                      isDisabled={!isEdited}
                    />
                    {errors.department && (
                      <FormFeedback className="d-block">
                        {errors.department.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">Municipio</Label>
              <Controller
                name="municipality"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={cities}
                      isDisabled={!isEdited}
                      onChange={(e) => {
                        field.onChange(e)
                        setValue('district', null)
                      }}
                    />
                    {errors.municipality && (
                      <FormFeedback className="d-block">
                        {errors.municipality.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <Label className="me-1">Distrito</Label>
              <Controller
                name="district"
                control={control}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      options={districtOptions}
                      isDisabled={!isEdited || !municipality?.value}
                    />
                    {errors.district && (
                      <FormFeedback className="d-block">
                        {errors.district.message}
                      </FormFeedback>
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            <Col
              md="6"
              sm="12"
            >
              <InputInternationalPhone
                label={'Teléfono*'}
                errors={errors}
                control={control}
                options={{
                  id: 'phoneNumber',
                  name: 'phoneNumber',
                  disabled: !isEdited,
                }}
              />
            </Col>
            <Col
              md="6"
              sm="12"
            >
              <InputText
                label={'Direccion'}
                errors={errors}
                disabled={true}
                options={{
                  control,
                  id: 'address',
                  name: 'address',
                  disabled: !isEdited,
                }}
              />
            </Col>
          </Row>
          <Row
            className="mx-0"
            style={{ paddingTop: '2%', paddingLeft: '5%' }}
          >
            {data?.logo && (
              <Col
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexDirection: 'column',
                }}
              >
                <Label
                  className="position-relative"
                  style={{ top: '0', left: '0' }}
                >
                  Logo
                </Label>
                <ListGroupItem className="d-flex align-items-center justify-content-between p-0 w-100">
                  <div className="file-details d-flex align-items-center">
                    <div className="file-preview me-1">
                      <img
                        className="rounded"
                        src={data?.logo}
                        style={{
                          maxHeight: '60px',
                          maxWidth: '60px',
                          objectFit: 'contain',
                        }}
                        alt="Logo"
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      height: '100%',
                      alignItems: 'center',
                      marginRight: '5px',
                    }}
                  >
                    <div
                      className="vr"
                      style={{
                        borderLeft: '2px solid #D8D7DB',
                        marginRight: '10px',
                      }}
                    ></div>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      className="btn-icon"
                      onClick={toggleModal}
                    >
                      <Eye size={14} />
                    </Button>
                    <Button
                      color="secondary"
                      style={{ marginLeft: '4px' }}
                      outline
                      size="sm"
                      className="btn-icon"
                      onClick={addLogo}
                    >
                      <Edit size={14} />
                    </Button>
                  </div>
                </ListGroupItem>
              </Col>
            )}
            <Col
              md="6"
              sm="12"
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              {!isEdited ? (
                <Button
                  style={{
                    size: '10px',
                    width: '200px',
                    marginTop: '5px',
                  }}
                  color="primary"
                  onClick={(e) => handleEdit(e)}
                >
                  Editar
                </Button>
              ) : (
                <Button
                  style={{
                    size: '10px',
                    width: '200px',
                    marginTop: '10px',
                  }}
                  color="primary"
                  type="submit"
                >
                  Guardar
                </Button>
              )}
            </Col>
          </Row>
        </Card>
      </Form>
      {/* Modal para visualizar la imagen */}
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
      >
        <ModalHeader toggle={toggleModal}>Logo</ModalHeader>
        <ModalBody>
          <img
            src={data?.logo}
            alt="Logo"
            style={{ width: '100%', height: 'auto' }}
          />
        </ModalBody>
      </Modal>
    </Fragment>
  )
}

export default BusinessForm
